import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, SecurityContext } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, delay, from, lastValueFrom, map, Observable, of, shareReplay, Subscription, throwError, timeout, withLatestFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
	CreatePalletBody,
	CreatePalletResponse,
	LikutPalette,
	LikutPaletteMoves,
	LikutPaletteMovesAllInventory,
	LikutPalletProducts,
	LikutPalletValidation,
	LikutPrinter,
	LikutWarehouse,
	LikutWarehouseAvailableButtons,
	LikutWarehouseButtons,
	LikutWarehouseInventoryDisplayWithScans,
	LikutWarehouseInventoryResponse,
	LikutWarehouseMoveResponse,
	LikutWarehousePalletMovementsByDateResponse,
	LikutWarehouseScanInventoryFinishBody,
	TransferPalletBody,
} from '../Interfaces/likut';
import { ResponseFromExpress } from '../Interfaces/SQL-interfaces';
import { AdminTypes } from '../Interfaces/users';
import { FormattedLog } from '../Utility/FormattedConsole';
import { getDateAsID } from '../Utility/functions';
import { LikutService } from './likut.service';
import { LOCALES, LocalesEnum, LocaleService, LOCALE_PREFERENCE } from './locale.service';
import { StoreService } from './store.service';

// TODO - Add the correct types, this is just a placeholder
export const LIKUT_WAREHOUSE_ADMIN_TYPES = ['melaketwarehouse'] as const;

export const allActions = ['reprint', 'palletSendOut', 'palletCreate', 'palletTransfer', 'inventoryScan', 'palletCreateWarehouseW', 'palletInfo'] as const;
export type AllLikutWarehouseActions = typeof allActions[number];

export const defaultButtonsText = {
	main_pallets: 'משטחים',

	// pallet-reprint
	'pallet-reprint': 'הדפסת תוויות',
	'pallet-reprint-title': 'הדפסת תוויות',
	'pallet-reprint-pallet-select-label': 'משטח',
	'pallet-reprint-pallet-select-placeholder': 'הקלד משטח',
	'pallet-reprint-printer-select-label': 'מדפסת',
	'pallet-reprint-printer-select-placeholder': 'בחר מדפסת',
	'pallet-reprint-confirm-button': 'שלח להדפסה',
	'pallet-reprint-success-message': 'ההדפסה נשלחה בהצלחה!',

	// pallet-transfer
	'pallet-transfer': 'העברת משטח',
	'pallet-transfer-title': 'העברת משטח',
	'pallet-transfer-confirm-button': 'המשך',
	'pallet-transfer-warehouse-select-label': 'מחסן',
	'pallet-transfer-warehouse-select-placeholder': 'בחר מחסן',
	'pallet-transfer-title-next-stage': 'העברת משטח',
	'pallet-transfer-title-next-stage-button': 'העבר',
	'pallet-transfer-palletID-label': 'משטח',
	'pallet-transfer-palletID-placeholder': 'הקלד משטח',
	'pallet-transfer-pallet-list-productID-label': 'קוד',
	'pallet-transfer-pallet-list-palletID-label': 'משטח',
	'pallet-transfer-pallet-list-tokef-label': 'תוקף',
	'pallet-transfer-success-message': 'המשטח הועבר בהצלחה!',

	// pallet-send-out
	'pallet-send-out': 'הוצאת משטח',
	'pallet-send-out-title': 'הוצאת משטח',
	'pallet-send-out-confirm-button': 'המשך',
	'pallet-send-out-palletID-label': 'משטח',
	'pallet-send-out-palletID-placeholder': 'הקלד משטח',
	'pallet-send-out-pallet-list-productID-label': 'קוד',
	'pallet-send-out-pallet-list-palletID-label': 'משטח',
	'pallet-send-out-pallet-list-tokef-label': 'תוקף',
	'pallet-send-out-success-message': 'המשטח הוצא בהצלחה!',

	// create-pallet
	'create-pallet': 'משטח חדש',
	'create-pallet-title': 'משטח חדש',
	'create-pallet-confirm-button': 'המשך',
	'create-pallet-product-select-label': 'פריט',
	'create-pallet-product-select-placeholder': 'בחר פריט',
	'create-pallet-barcode-label': 'ברקוד',
	'create-pallet-productID-label': 'קוד פריט',
	'create-pallet-itempack-label': 'כמות בקרטון',
	'create-pallet-tokef-datepicker-label': 'תוקף',
	'create-pallet-packperpallet-label': 'אריזות במשטח',
	'create-pallet-extra4-label': 'הערה',
	'create-pallet-extra4-placeholder': 'הקלד הערה',
	'create-pallet-warehouse-select-label': 'מחסן',
	'create-pallet-warehouse-select-placeholder': 'בחר מחסן',
	'create-pallet-printer-select-label': 'מדפסת',
	'create-pallet-printer-select-placeholder': 'בחר מדפסת',
	'create-pallet-palletnum-label': 'מס משטחים',
	'create-pallet-next-stage-button': 'צור משטח',
	'create-pallet-success-message': 'המשטח נוצר בהצלחה!',
	'create-pallet-success-palletIDs': 'מספרי משטחים',

	// create-warehouse-w
	'create-warehouse-w': 'משטח חדש W',
	'create-warehouse-w-title': 'משטח חדש W',

	// inventory-scan
	'inventory-scan': 'ספירת מלאי',
	'inventory-scan-title': 'ספירת מלאי',
	'inventory-scan-confirm-button': 'המשך',
	'inventory-scan-warehouse-select-label': 'מחסן',
	'inventory-scan-warehouse-select-placeholder': 'בחר מחסן',
	'inventory-scan-productID-header': 'מק"ט',
	'inventory-scan-product-name-header': 'שם פריט',
	'inventory-scan-warehouse-header': 'מחסן',
	'inventory-scan-quantity-header': 'מלאי',
	'inventory-scan-tokef-header': 'תוקף',
	'inventory-scan-packs-header': "כמות אר'",
	'inventory-scan-palletID-header': 'מזהה משטח',
	'inventory-scan-totalScan-header': 'נסרק',
	'inventory-scan-request-error': 'לא ניתן להציג את המלאי',
	'inventory-scan-warehouse-label': 'מחסן',
	'inventory-scan-palletID-label': 'משטח',
	'inventory-scan-tokef-label': 'תוקף',
	'inventory-scan-packQTY-label': 'כמות',
	'inventory-scan-isScanned-label': 'סריקה',
	'inventory-scan-no-pallet-found': 'לא נמצא משטח עם המזהה הזה',
	'inventory-scan-history-button-title': 'הצג סריקות',
	'inventory-scan-history-popup-title': 'סריקות',
	'inventory-scan-confirm-send': 'האם הנך בטוח שתרצה לסיים? פעולה זו סופית ואינה ניתנת לביטול',
	'inventory-scan-scan-action': 'סרוק',
	'inventory-scan-send-action': 'שלח',
	'inventory-scan-barcode-label': 'ברקוד',

	// pallet-info
	'pallet-info': 'בדיקת משטח',
	'pallet-info-title': 'בדיקת משטח',
	'pallet-info-confirm-button': 'המשך',
	'pallet-info-create-datepicker-label': 'תאריך יצירה',
	'pallet-info-user-label': 'משתמש',

	// snackbar
	'snackbar-pallet-already-in-list': 'משטח זה כבר קיים ברשימה זאת',
	'snackbar-pallet-cant-be-added': 'משטח זה אינו ניתן להעברה',
	'snackbar-network-error': 'ישנה תקלת תקשורת, נסו שנית',
	'snackbar-multiple-products-found': 'נמצאו מספר מוצרים עם ברקוד זה',
	'snackbar-product-not-found': 'לא נמצא מוצר עם הברקוד הזה',
	'snackbar-pallet-scanned': 'המשטח נסרק בהצלחה',
	'snackbar-pallet-scan-removed': 'המשטח הוסר מהרשימה בהצלחה',
	'snackbar-inventory-scan-sent': 'הבקשה נשלחה בהצלחה. מזהה תנועה: ',

	//inventory-report
	main_inventory_report: 'מלאי זמין',
	'inventory-report-reset': 'איפוס',
	'inventory-report-search-placeholder': 'חפש',
	'inventory-report-no-product-name': 'ללא שם',
	'inventory-report-palletID-label': 'משטח',
	'inventory-report-tokef-label': 'תאריך תוקף',
	'inventory-report-packQTY-label': 'כמות',
	'inventory-report-remark-label': 'הערה',
	'inventory-report-warehouse-select-label': 'מחסנים',
	'inventory-report-request-error': 'לא ניתן להציג את המלאי',
	'inventory-report-productID-header': 'מק"ט',
	'inventory-report-product-name-header': 'שם פריט',
	'inventory-report-warehouse-header': 'מחסן',
	'inventory-report-quantity-header': 'מלאי',
	'inventory-report-instock-header': 'משטחים',
	'inventory-report-tokef-header': 'תוקף',
	'inventory-report-remark-header': 'הערה',

	//all-inventory-report
	'all-inventory-report': 'ספירות מלאי',
	'all-inventory-report-title': 'ספירות מלאי',
	'all-inventory-report-ID-header': 'מזהה',
	'all-inventory-report-userID-header': 'סוכן',
	'all-inventory-report-warehouse-header': 'מחסן',
	'all-inventory-report-create-date-header': 'תאריך יצירה',
	'all-inventory-report-productID-header': 'מק"ט',
	'all-inventory-report-palletID-header': 'משטח',
	'all-inventory-report-product-name-header': 'שם פריט',
	'all-inventory-report-org-warehouseID-header': 'מחסן',

	//pallet-movements-report
	'pallet-movements-report': 'סטטוס משטחים',
	'pallet-movements-report-title': 'סטטוס משטחים',
	'pallet-movements-report-palletID-header': 'משטח',
	'pallet-movements-report-productID-header': 'מק"ט',
	'pallet-movements-report-product-name-header': 'שם פריט',
	'pallet-movements-report-warehouseID-header': 'מחסן',
	'pallet-movements-report-moveID-label': 'מזהה תנועה',
	'pallet-movements-report-count-label': 'סוג תנועה',
	'pallet-movements-report-move-date-label': 'תאריך',
	'pallet-movements-report-userID-label': 'משתמש',
	'pallet-movements-report-remark-header': 'הערה',

	//warehouse-manage
	'warehouse-manage': 'ניהול מחסנים',
	'warehouse-manage-title': 'ניהול מחסנים',
	'warehouse-manage-add-button': 'הוסף מחסן',
	'warehouse-manage-add-title': 'הוספת מחסן',
	'warehouse-manage-add-confirm-button': 'הוסף',
	'warehouse-manage-add-name-label': 'שם מחסן',
	'warehouse-manage-add-warehouseID-label': 'מזהה מחסן',

	// date-range-pallet-movement-report
	'date-range-pallet-movement-report': 'תנועות משטחים',
	'date-range-pallet-movement-report-title': 'תנועות משטחים',
	'date-range-pallet-movement-report-moveID-header': 'מזהה תנועה',
	'date-range-pallet-movement-report-count-header': 'סוג תנועה',
	'date-range-pallet-movement-report-move-date-header': 'תאריך',
	'date-range-pallet-movement-report-warehouseID-header': 'מחסן',
	'date-range-pallet-movement-report-userID-header': 'משתמש',
	'date-range-pallet-movement-report-palletID-header': 'משטח',
	'date-range-pallet-movement-report-productID-header': 'מק"ט',
	'date-range-pallet-movement-report-product-name-header': 'שם פריט',
	'date-range-pallet-movement-report-userID-select-label': 'משתמשים',

	// general
	'general-error-sending-request': 'אירעה שגיאה בשליחת הבקשה',
	'close-button': 'סגור',
	'required-field': 'שדה חובה',
	'dialog-close-confirmation': 'האם הנך בטוח שתרצה לצאת?',
} as const;

export type ButtonTextPossibleValues = keyof typeof defaultButtonsText;

@Injectable({
	providedIn: 'root',
})
export class LikutWarehouseService {
	private _ss = inject(StoreService);
	private router = inject(Router);
	private http = inject(HttpClient);
	private _locale = inject(LocaleService);
	constructor() {
		this.init();
	}

	private Admin: AdminTypes | null = null;

	likutWarehouseAdminTypes: Record<typeof LIKUT_WAREHOUSE_ADMIN_TYPES[number], boolean> = LIKUT_WAREHOUSE_ADMIN_TYPES.reduce((p, c) => {
		p[c] = false;
		return p;
	}, {} as Record<typeof LIKUT_WAREHOUSE_ADMIN_TYPES[number], boolean>);

	isLikutWarehouseAppActive$ = new BehaviorSubject<boolean>(false);

	specificMovementInfoSelected$ = new BehaviorSubject<LikutWarehouseMoveResponse | null>(null);

	lastTimePerformedAction: Date | null = null;

	// warehouses = []
	warehouses$ = new BehaviorSubject<LikutWarehouse[]>([]);
	warehousesAll$ = new BehaviorSubject<LikutWarehouse[]>([]);
	warehouseInLocalStorage$ = new BehaviorSubject<string | null>(null);
	lastWarehouseSelected$ = combineLatest([this.warehouses$, this.warehouseInLocalStorage$]).pipe(
		map(([warehouses, warehouseInLocalStorage]) => warehouses.find(w => w.WarehouseID === warehouseInLocalStorage) || null),
	);

	printers$ = new BehaviorSubject<LikutPrinter[]>([]);
	printerInLocalStorage$ = new BehaviorSubject<number | null>(null);
	lastPrinterSelected$ = combineLatest([this.printers$, this.printerInLocalStorage$]).pipe(
		map(([printers, printerInLocalStorage]) => printers.find(p => p.PrinterID === printerInLocalStorage) || null),
	);

	products$ = new BehaviorSubject<LikutPalletProducts[]>([]);
	productsDictionary$ = this.products$.pipe(
		map(products =>
			products.reduce((p, c) => {
				p[c.ProductId] = c;
				return p;
			}, {} as Record<string, LikutPalletProducts>),
		),
	);

	inventoryScanSelectedWarehouse$ = new BehaviorSubject<string | null>(null);

	actionsAvailable: Record<AllLikutWarehouseActions, boolean> = {
		reprint: false,
		palletSendOut: false,
		palletCreate: false,
		palletCreateWarehouseW: false,
		palletTransfer: false,
		palletInfo: false,
		inventoryScan: false,
	};

	actionsButtons$ = new BehaviorSubject<LikutWarehouseButtons[]>([]);

	actionButtonsAvailable$ = new BehaviorSubject<LikutWarehouseAvailableButtons>({});

	forcedButtonsAndTextTypeof$ = new BehaviorSubject<LocalesEnum | null>(localStorage.getItem('BUTTONS_TEXT_TYPEOF') as LocalesEnum | null);

	buttonsAndText$ = combineLatest([this.actionsButtons$, this.forcedButtonsAndTextTypeof$, this.actionButtonsAvailable$]).pipe(
		map(([buttons, forcedType, actionButtonsAvailable]) => {
			const shouldFilterButtons = Object.keys(actionButtonsAvailable).length > 0;
			const defaultValues = Object.entries(defaultButtonsText).reduce((p, [k, v]) => {
				p[k] = { main: v, sub: '', shouldFilterButton: shouldFilterButtons && !actionButtonsAvailable[k] };
				return p;
			}, {} as Record<keyof typeof defaultButtonsText, { main: string; sub: string; shouldFilterButton: boolean }>);
			return buttons.reduce((p, c) => {
				if (forcedType) {
					c.typeof = forcedType;
				}

				p[c.button_id] = {
					main: c.typeof === LOCALE_PREFERENCE.HEBREW || c.typeof === LOCALE_PREFERENCE.DISPLAY_BOTH ? defaultValues[c.button_id].main : c.text,
					sub: c.typeof === LOCALE_PREFERENCE.DISPLAY_BOTH ? c.text : '',
					shouldFilterButton: p[c.button_id].shouldFilterButton,
				};

				return p;
			}, defaultValues as Record<keyof typeof defaultButtonsText, { main: string; sub: string; shouldFilterButton: boolean }>);
		}),
	);

	readonly apiEntry = '/mobil/likut/warehouse';

	// isLoadingPalletCheck$ = new BehaviorSubject<boolean>(false);

	init() {
		this.updateParameters();

		const printer = localStorage.getItem('lastPrinterSelected');
		const warehouse = localStorage.getItem('lastWarehouseSelected');
		if (printer) {
			this.printerInLocalStorage$.next(Number(printer));
		}
		if (warehouse) {
			this.warehouseInLocalStorage$.next(warehouse);
		}
	}

	updateParameters() {
		this.updateAdminType();
		this.updateActions();
		this.updateLastTimePerformedAction();
		this.updateAvailableButtons();
	}

	updateLocalData() {
		this.updatePrinters();
		this.updateWarehouses();
		this.updateWarehousesAll();
		this.updateProducts();
		this.updateButtons();
	}

	private updateLastTimePerformedAction() {
		if (this.lastTimePerformedAction) return;
		const time = localStorage.getItem('lastTimePerformedAction');

		this.lastTimePerformedAction = new Date(time ?? Date.now());
	}

	private overrideLastTimePerformedAction() {
		this.lastTimePerformedAction = new Date();
		localStorage.setItem('lastTimePerformedAction', this.lastTimePerformedAction.toString());
	}

	private updateActions() {
		if (!this._ss.CustomerInformation?.LikutWarehouse_Actions) return;

		const { LikutWarehouse_Actions } = this._ss.CustomerInformation;
		const split = LikutWarehouse_Actions.split('_');

		for (const action of Object.keys(this.actionsAvailable)) {
			this.actionsAvailable[action] = split.includes(action);
		}
	}

	private updateAdminType() {
		this.Admin ??= localStorage.getItem('Admin') as AdminTypes;

		if (!this.Admin) {
			return;
		}

		for (const admin of LIKUT_WAREHOUSE_ADMIN_TYPES) {
			const isAdmin = this.isAdminLowerCase(admin);
			this.likutWarehouseAdminTypes[admin] = isAdmin;
		}

		this.isLikutWarehouseAppActive$.next(Object.values(this.likutWarehouseAdminTypes).some(admin => admin));
	}
	// private likutAdminState(propertyToLookup: typeof LIKUT_WAREHOUSE_ADMIN_TYPES[number]) {
	// 	const val = this.likutWarehouseAdminTypes[propertyToLookup];
	// 	if (!this.isLikutWarehouseAppActive$.value) {
	// 		this.isLikutWarehouseAppActive$.next(val);
	// 	}
	// 	return val;
	// }

	private isAdminLowerCase(value: typeof LIKUT_WAREHOUSE_ADMIN_TYPES[number]) {
		if (!this.Admin) return false;
		return this.Admin.toLowerCase().includes(value);
	}

	updatePrinters() {
		const shouldDisconnect = this.checkIfShouldDisconnect();
		if (shouldDisconnect) {
			localStorage.clear();
			this.router.navigate(['login']);
			return;
		}
		this._ss.get<LikutPrinter>(`${this.apiEntry}/printers`).subscribe({
			next: res => {
				if (typeof res === 'string') {
					console.error(res);
					return;
				}
				this.overrideLastTimePerformedAction();
				this.printers$.next(res.recordset);
			},
			error: err => {
				console.error(err);
				alert('שגיאה בטעינת מדפסות');
			},
		});
	}

	updateWarehouses() {
		const shouldDisconnect = this.checkIfShouldDisconnect();
		if (shouldDisconnect) {
			localStorage.clear();
			this.router.navigate(['login']);
			return;
		}
		this._ss.get<LikutWarehouse>(`${this.apiEntry}/warehouses`).subscribe({
			next: res => {
				if (typeof res === 'string') {
					console.error(res);
					return;
				}
				this.overrideLastTimePerformedAction();
				this.warehouses$.next(res.recordset);
			},
			error: err => {
				console.error(err);
				alert('שגיאה בטעינת מחסנים');
			},
		});
	}
	updateWarehousesAll() {
		const shouldDisconnect = this.checkIfShouldDisconnect();
		if (shouldDisconnect) {
			localStorage.clear();
			this.router.navigate(['login']);
			return;
		}
		this._ss.get<LikutWarehouse>(`${this.apiEntry}/warehouses-all`).subscribe({
			next: res => {
				if (typeof res === 'string') {
					console.error(res);
					return;
				}
				this.overrideLastTimePerformedAction();
				this.warehousesAll$.next(res.recordset);
			},
			error: err => {
				console.error(err);
				alert('שגיאה בטעינת מחסנים');
			},
		});
	}

	updateProducts() {
		const shouldDisconnect = this.checkIfShouldDisconnect();
		if (shouldDisconnect) {
			localStorage.clear();
			this.router.navigate(['login']);
			return;
		}
		this._ss.get<LikutPalletProducts>(`${this.apiEntry}/products`).subscribe({
			next: res => {
				if (typeof res === 'string') {
					console.error(res);
					return;
				}
				this.overrideLastTimePerformedAction();
				this.products$.next(res.recordset);
			},
			error: err => {
				console.error(err);
				alert('שגיאה בטעינת מוצרים');
			},
		});
	}

	updateButtons() {
		const shouldDisconnect = this.checkIfShouldDisconnect();
		if (shouldDisconnect) {
			localStorage.clear();
			this.router.navigate(['login']);
			return;
		}

		this._ss.get<LikutWarehouseButtons>(`${this.apiEntry}/buttons`).subscribe({
			next: res => {
				if (typeof res === 'string') {
					console.error(res);
					return;
				}

				this.overrideLastTimePerformedAction();
				this.actionsButtons$.next(res.recordset);
			},
			error: err => {},
		});
	}

	updateAvailableButtons() {
		const shouldDisconnect = this.checkIfShouldDisconnect();
		if (shouldDisconnect || !this._ss.CustomerInformation) {
			localStorage.clear();
			this.router.navigate(['login']);
			return;
		}

		this._ss.get<{ AgentID: string; Button: string }>(`${this.apiEntry}/allowed-buttons`).subscribe({
			next: res => {
				if (typeof res === 'string') {
					console.error(res);
					return;
				}

				if (environment.development) {
					FormattedLog.log({ area: 'LikutWarehouseService', message: 'Development mode - all buttons are available' });
					res.recordset = [];
				}

				this.overrideLastTimePerformedAction();
				this.actionButtonsAvailable$.next(
					res.recordset
						.filter(r => r.AgentID === this._ss.AgentIdConnected)
						.reduce((p, c) => {
							p[c.Button] = true;
							return p;
						}, {} as Record<string, boolean>),
				);
			},
			error: err => {},
		});
	}

	async forceUpdateRemoteDB() {
		return await new Promise<void>((resolve, reject) => {
			fetch(`${this._ss.CustomerInformation.CustServer}${this.apiEntry}/updateLocals`)
				.then(res => {
					if (res.ok) {
						resolve();
					} else {
						reject();
					}
				})
				.catch(reject);
		});
	}

	sendReprintRequest(palletID: string, printerID: string) {
		if (environment.development) {
			FormattedLog.log({ area: 'LikutWarehouseService', message: 'Development mode - no transfer request sent' });
			return of({ recordset: [] }).pipe(delay(2000)) as Observable<ResponseFromExpress<any>>;
		}
		const shouldDisconnect = this.checkIfShouldDisconnect();
		if (shouldDisconnect) {
			localStorage.clear();
			this.router.navigate(['login']);
			return of();
		}
		this.overrideLastTimePerformedAction();
		return this._ss.put(`${this.apiEntry}/reprint/${palletID}/${printerID}`, {});
	}

	sendCreatePalletRequest(body: CreatePalletBody) {
		if (environment.development) {
			FormattedLog.log({ area: 'LikutWarehouseService', message: 'Development mode - no transfer request sent' });
			return of({ err: false, msg: 'הבקשה נשלחה בהצלחה', palletIDs: [0] }).pipe(delay(2000));
		}
		const shouldDisconnect = this.checkIfShouldDisconnect();
		if (shouldDisconnect) {
			localStorage.clear();
			this.router.navigate(['login']);
			return of();
		}
		this.overrideLastTimePerformedAction();
		return this.http.post<CreatePalletResponse>(`${this._ss.CustomerInformation.CustServer}${this.apiEntry}/pallets`, body);
	}

	sendTransferPalletRequest(body: TransferPalletBody[]) {
		if (environment.development) {
			FormattedLog.log({ area: 'LikutWarehouseService', message: 'Development mode - no transfer request sent' });
			return of({ recordset: [] }).pipe(delay(2000));
		}
		const shouldDisconnect = this.checkIfShouldDisconnect();
		if (shouldDisconnect) {
			localStorage.clear();
			this.router.navigate(['login']);
			return of();
		}
		this.overrideLastTimePerformedAction();
		return this.http.post(`${this._ss.CustomerInformation.CustServer}${this.apiEntry}/pallet-moves`, body);
	}

	checkIfShouldDisconnect(): boolean {
		const disconnectionTimeoutParameter = this._ss.CustomerInformation?.LikutWarehouse_DisconnectionTimeout;

		if (!this.lastTimePerformedAction) {
			this.updateLastTimePerformedAction();
		}

		if (!disconnectionTimeoutParameter || disconnectionTimeoutParameter === '0' || !this.lastTimePerformedAction) {
			return false;
		}

		const dateToCheckAgainst = new Date(Date.now() - Number(disconnectionTimeoutParameter) * 60 * 1000);

		return this.lastTimePerformedAction < dateToCheckAgainst;
	}

	checkIfPalletIDValid(palletID: string | number) {
		return this._ss.get<LikutPalletValidation>(`${this.apiEntry}/pallets/${palletID}/is-pallet-ok`).pipe(
			map((res): LikutPalletValidation | null => {
				if (typeof res === 'string') {
					console.error(res);
					return null;
				}
				return res.recordset[0];
			}),
		);
	}

	checkIfInventoryScanPalletIDValid(palletID: string | number) {
		return this._ss.get<LikutPalletValidation>(`${this.apiEntry}/pallets/${palletID}/is-pallet-ok/scan-inventory-check`).pipe(
			map(res => {
				if (typeof res === 'string') {
					console.error(res);
					return null;
				}
				return res.recordset[0];
			}),
		);
	}

	getPallet(palletID: string | number) {
		return this._ss.get<LikutPalette>(`${this.apiEntry}/pallets/${palletID}`).pipe(
			map(res => {
				if (typeof res === 'string') {
					console.error(res);
					return null;
				}
				return res.recordset[0];
			}),
		);
	}

	getPalletInfoAfterScan(palletID: string | number) {
		return combineLatest([this.checkIfInventoryScanPalletIDValid(palletID), this.getPallet(palletID)]).pipe(
			map(([palletValidation, pallet]) => {
				if (!palletValidation || !pallet) {
					return null;
				} else {
					return { ...palletValidation, ...pallet };
				}
			}),
		);
	}

	getPalletInventoryReport() {
		return this._ss.get<LikutWarehouseInventoryResponse>(`${this.apiEntry}/reports/inventory`);
	}
	getAllInventoryReport() {
		return this._ss.get<LikutWarehouseInventoryResponse>(`${this.apiEntry}/reports/all-inventory`);
	}
	getAllInventoryReportMovements(movementID: string | number) {
		return this._ss.get<LikutPaletteMovesAllInventory>(`${this.apiEntry}/reports/all-inventory/${movementID}`);
	}

	getAllPalletMovementsReport() {
		return this._ss.get<LikutPalletValidation>(`${this.apiEntry}/reports/pallet-movements`);
	}

	getPalletMovementsReport(palletID: string | number) {
		return this._ss.get<LikutPaletteMoves>(`${this.apiEntry}/reports/pallet-movements/${palletID}`);
	}

	getInventoryScanReport() {
		if (!this.inventoryScanSelectedWarehouse$.value) {
			return of(null);
		}
		const warehouseID = this.inventoryScanSelectedWarehouse$.value;
		return this._ss.get<LikutWarehouseInventoryResponse>(`${this.apiEntry}/reports/inventory-scan/${warehouseID}`);
	}

	prepareDataForSendInventoryScanReport(scanHistory: LikutWarehouseInventoryResponse[]): LikutWarehouseScanInventoryFinishBody {
		const WarehouseID = this.inventoryScanSelectedWarehouse$.value;
		return {
			DocID: getDateAsID(),
			WarehouseID,
			UserID: this._ss.AgentIdConnected,
			lines: scanHistory.map(p => ({
				ProductID: p.ProductId,
				PaletteID: p.PaletteID,
				PackQTY: p.PackQTY,
			})),
		};
	}

	sendInventoryScanReport(body: LikutWarehouseScanInventoryFinishBody) {
		if (environment.development) {
			return of({ err: false, msg: 'הבקשה נשלחה בהצלחה', lastID: 0 }).pipe(delay(3000));
		}
		return from(
			fetch(`${this._ss.CustomerInformation.CustServer}${this.apiEntry}/reports/inventory-scan-finish`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(body),
			}).then(async res => {
				if (!res.ok) {
					if (res.status === 400) {
						throw new Error('לא ניתן לסיים תהליך זה, אנא נסו שנית מאוחר יותר');
					}
					throw new Error('ישנה תקלת תקשורת, נסו שנית מאוחר יותר');
				}
				const data: { err: false; msg: string; lastID: number } = await res.json();
				return data;
			}),
		);
	}

	getPalletMovementReportByDateRange(fromDate: Date, toDate: Date) {
		const from = fromDate.toISOString();
		const to = toDate.toISOString();
		let params = new HttpParams({ fromObject: { from, to } });
		return this._ss.get<LikutWarehousePalletMovementsByDateResponse>(`${this.apiEntry}/reports/date-range-pallet-movement`, params);
	}

	validateLikutPalletProduct(control: FormControl) {
		if (!control.value || !this.products$.value.some(p => p.ProductId === control.value)) {
			return { noBank: true };
		}
		return null;
	}

	createNewWarehouse(body: { WarehouseID: string; WarehouseName: string; Type?: string; Details?: string }) {
		return this._ss.post(`${this.apiEntry}/warehouses`, body);
	}

	updateWarehouse(warehouseID: string, body: { WarehouseName: string; Type?: string; Details?: string }) {
		return this._ss.put(`${this.apiEntry}/warehouses/${warehouseID}`, body);
	}

	deleteWarehouse(warehouseID: string) {
		return this._ss.delete(`${this.apiEntry}/warehouses/${warehouseID}`);
	}

	updateButtonsTextFrocedTypeof(type: LocalesEnum | null) {
		this.forcedButtonsAndTextTypeof$.next(type);
		if (type === null) {
			localStorage.removeItem('BUTTONS_TEXT_TYPEOF');
		} else {
			localStorage.setItem('BUTTONS_TEXT_TYPEOF', type);
		}
		this._locale.setLocale(LOCALES[type] ?? null);
	}

	refreshObjectReferenceForButtonsTextTypeof() {
		const newObject = {};
		for (const [key, value] of Object.entries(this.actionButtonsAvailable$.value)) {
			newObject[key] = value;
		}
		this.actionButtonsAvailable$.next(newObject);
	}
}
